import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from './firebase';
import { CREW_JOBS_API_BASE } from './crewJobsPublicApi';
import {
  attributionFromSearchParams,
  summarizeCrewJobsAnalytics,
  type CrewJobsAnalyticsAttribution,
  type CrewJobsAnalyticsEventName,
  type CrewJobsAnalyticsRawRow,
  type CrewJobsAnalyticsSummary,
} from './crewJobsAnalyticsHelpers';

export type { CrewJobsAnalyticsAttribution, CrewJobsAnalyticsEventName, CrewJobsAnalyticsRawRow, CrewJobsAnalyticsSummary };
export {
  attributionFromSearchParams,
  CREW_JOBS_ANALYTICS_EVENTS,
  summarizeCrewJobsAnalytics,
} from './crewJobsAnalyticsHelpers';

export type CrewJobsTrackPayload = {
  event: CrewJobsAnalyticsEventName;
  path: string;
  jobId?: string | null;
  jobSlug?: string | null;
  jobTitle?: string | null;
};

const CLIENT_ID_KEY = 'lctf_crew_vid';
const ATTR_SESSION_KEY = 'lctf_crew_attr';
const SESSION_ID_KEY = 'lctf_crew_sid';

function randomId(prefix: string): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return `${prefix}_${crypto.randomUUID().replace(/-/g, '').slice(0, 24)}`;
    }
  } catch {
    /* ignore */
  }
  return `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

function readStorage(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

/** Anonymous visitor id (localStorage). No PII. */
export function getCrewJobsAnonymousClientId(): string {
  if (typeof window === 'undefined') return randomId('vid');
  const existing = readStorage(window.localStorage, CLIENT_ID_KEY);
  if (existing && /^[a-zA-Z0-9_-]{8,64}$/.test(existing)) return existing;
  const next = randomId('vid');
  writeStorage(window.localStorage, CLIENT_ID_KEY, next);
  return next;
}

function getSessionId(): string {
  if (typeof window === 'undefined') return randomId('sess');
  const existing = readStorage(window.sessionStorage, SESSION_ID_KEY);
  if (existing && /^[a-zA-Z0-9_-]{8,64}$/.test(existing)) return existing;
  const next = randomId('sess');
  writeStorage(window.sessionStorage, SESSION_ID_KEY, next);
  return next;
}

function attributionHasValue(attr: CrewJobsAnalyticsAttribution): boolean {
  return Boolean(attr.utmSource || attr.utmMedium || attr.utmCampaign || attr.ref || attr.from);
}

/** Capture UTMs / ref / from from the current URL; keep them for the browser session. */
export function captureCrewJobsAttribution(): CrewJobsAnalyticsAttribution {
  if (typeof window === 'undefined') {
    return { utmSource: null, utmMedium: null, utmCampaign: null, ref: null, from: null };
  }
  const fromUrl = attributionFromSearchParams(new URLSearchParams(window.location.search));
  if (attributionHasValue(fromUrl)) {
    writeStorage(window.sessionStorage, ATTR_SESSION_KEY, JSON.stringify(fromUrl));
    return fromUrl;
  }
  try {
    const raw = readStorage(window.sessionStorage, ATTR_SESSION_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as CrewJobsAnalyticsAttribution;
      if (parsed && typeof parsed === 'object') return parsed;
    }
  } catch {
    /* ignore */
  }
  return fromUrl;
}

/**
 * Fire-and-forget first-party analytics. Never throws; never blocks the board.
 */
export function trackCrewJobsEvent(payload: CrewJobsTrackPayload): void {
  try {
    if (typeof window === 'undefined') return;
    const attr = captureCrewJobsAttribution();
    const body = JSON.stringify({
      event: payload.event,
      path: payload.path,
      jobId: payload.jobId || null,
      jobSlug: payload.jobSlug || null,
      jobTitle: payload.jobTitle || null,
      clientId: getCrewJobsAnonymousClientId(),
      sessionId: getSessionId(),
      utmSource: attr.utmSource,
      utmMedium: attr.utmMedium,
      utmCampaign: attr.utmCampaign,
      ref: attr.ref,
      from: attr.from,
    });

    const url = `${CREW_JOBS_API_BASE}/trackCrewJobsEvent`;
    // text/plain avoids CORS preflight issues with sendBeacon while CF still JSON-parses the body.
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
      const sent = navigator.sendBeacon(url, blob);
      if (sent) return;
    }

    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body,
      keepalive: true,
      mode: 'cors',
    }).catch(() => {
      /* fail open */
    });
  } catch {
    /* fail open */
  }
}

/** Staff-only: load recent analytics rows from Firestore (rules: CRM employee/admin). */
export async function fetchCrewJobsAnalyticsRows(days = 30, maxRows = 2500): Promise<CrewJobsAnalyticsRawRow[]> {
  const since = Timestamp.fromDate(new Date(Date.now() - days * 24 * 60 * 60 * 1000));
  const q = query(
    collection(db, 'crewJobsAnalytics'),
    where('createdAt', '>=', since),
    orderBy('createdAt', 'desc'),
    limit(maxRows),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => {
    const data = d.data() || {};
    const createdAt = data.createdAt;
    return {
      event: String(data.event || ''),
      path: data.path ? String(data.path) : null,
      jobId: data.jobId ? String(data.jobId) : null,
      jobSlug: data.jobSlug ? String(data.jobSlug) : null,
      jobTitle: data.jobTitle ? String(data.jobTitle) : null,
      clientId: data.clientId ? String(data.clientId) : null,
      utmSource: data.utmSource ? String(data.utmSource) : null,
      utmMedium: data.utmMedium ? String(data.utmMedium) : null,
      utmCampaign: data.utmCampaign ? String(data.utmCampaign) : null,
      ref: data.ref ? String(data.ref) : null,
      from: data.from ? String(data.from) : null,
      createdAtMs: createdAt?.toMillis ? createdAt.toMillis() : undefined,
    };
  });
}

export async function loadCrewJobsAnalyticsSummary(days = 30): Promise<CrewJobsAnalyticsSummary> {
  const rows = await fetchCrewJobsAnalyticsRows(days);
  return summarizeCrewJobsAnalytics(rows, days);
}
